/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getBooleanDecoder,
  getBooleanEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import { STAKE_POOL_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const INITIALIZE_POOL_DISCRIMINATOR = 0;

export function getInitializePoolDiscriminatorBytes() {
  return getU8Encoder().encode(INITIALIZE_POOL_DISCRIMINATOR);
}

export type InitializePoolInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountStakeMint extends string | AccountMeta<string> = string,
  TAccountRewardMint extends string | AccountMeta<string> = string,
  TAccountStakeVault extends string | AccountMeta<string> = string,
  TAccountRewardVault extends string | AccountMeta<string> = string,
  TAccountPayer extends string | AccountMeta<string> = string,
  TAccountTokenProgram extends
    | string
    | AccountMeta<string> = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
  TAccountSystemProgram extends
    | string
    | AccountMeta<string> = '11111111111111111111111111111111',
  TAccountRent extends
    | string
    | AccountMeta<string> = 'SysvarRent111111111111111111111111111111111',
  TAccountProgramAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountStakeMint extends string
        ? ReadonlyAccount<TAccountStakeMint>
        : TAccountStakeMint,
      TAccountRewardMint extends string
        ? ReadonlyAccount<TAccountRewardMint>
        : TAccountRewardMint,
      TAccountStakeVault extends string
        ? WritableAccount<TAccountStakeVault>
        : TAccountStakeVault,
      TAccountRewardVault extends string
        ? WritableAccount<TAccountRewardVault>
        : TAccountRewardVault,
      TAccountPayer extends string
        ? WritableSignerAccount<TAccountPayer> &
            AccountSignerMeta<TAccountPayer>
        : TAccountPayer,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      TAccountRent extends string
        ? ReadonlyAccount<TAccountRent>
        : TAccountRent,
      TAccountProgramAuthority extends string
        ? ReadonlyAccount<TAccountProgramAuthority>
        : TAccountProgramAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type InitializePoolInstructionData = {
  discriminator: number;
  poolId: bigint;
  rewardRate: bigint;
  minStakeAmount: bigint;
  lockupPeriod: bigint;
  enforceLockup: boolean;
  poolEndDate: Option<bigint>;
};

export type InitializePoolInstructionDataArgs = {
  poolId: number | bigint;
  rewardRate: number | bigint;
  minStakeAmount: number | bigint;
  lockupPeriod: number | bigint;
  enforceLockup: boolean;
  poolEndDate: OptionOrNullable<number | bigint>;
};

export function getInitializePoolInstructionDataEncoder(): Encoder<InitializePoolInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', getU8Encoder()],
      ['poolId', getU64Encoder()],
      ['rewardRate', getU64Encoder()],
      ['minStakeAmount', getU64Encoder()],
      ['lockupPeriod', getI64Encoder()],
      ['enforceLockup', getBooleanEncoder()],
      ['poolEndDate', getOptionEncoder(getI64Encoder())],
    ]),
    (value) => ({ ...value, discriminator: INITIALIZE_POOL_DISCRIMINATOR })
  );
}

export function getInitializePoolInstructionDataDecoder(): Decoder<InitializePoolInstructionData> {
  return getStructDecoder([
    ['discriminator', getU8Decoder()],
    ['poolId', getU64Decoder()],
    ['rewardRate', getU64Decoder()],
    ['minStakeAmount', getU64Decoder()],
    ['lockupPeriod', getI64Decoder()],
    ['enforceLockup', getBooleanDecoder()],
    ['poolEndDate', getOptionDecoder(getI64Decoder())],
  ]);
}

export function getInitializePoolInstructionDataCodec(): Codec<
  InitializePoolInstructionDataArgs,
  InitializePoolInstructionData
> {
  return combineCodec(
    getInitializePoolInstructionDataEncoder(),
    getInitializePoolInstructionDataDecoder()
  );
}

export type InitializePoolInput<
  TAccountPool extends string = string,
  TAccountStakeMint extends string = string,
  TAccountRewardMint extends string = string,
  TAccountStakeVault extends string = string,
  TAccountRewardVault extends string = string,
  TAccountPayer extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountRent extends string = string,
  TAccountProgramAuthority extends string = string,
> = {
  /** The stake pool PDA */
  pool: Address<TAccountPool>;
  /** The token mint being staked */
  stakeMint: Address<TAccountStakeMint>;
  /** The reward token mint */
  rewardMint: Address<TAccountRewardMint>;
  /** The pool's stake token vault */
  stakeVault: Address<TAccountStakeVault>;
  /** The pool's reward token vault */
  rewardVault: Address<TAccountRewardVault>;
  /** The account paying for rent (must be authorized admin) */
  payer: TransactionSigner<TAccountPayer>;
  /** The token program */
  tokenProgram?: Address<TAccountTokenProgram>;
  /** The system program */
  systemProgram?: Address<TAccountSystemProgram>;
  /** Rent sysvar */
  rent?: Address<TAccountRent>;
  /** The program authority account (validates creator permission) */
  programAuthority: Address<TAccountProgramAuthority>;
  poolId: InitializePoolInstructionDataArgs['poolId'];
  rewardRate: InitializePoolInstructionDataArgs['rewardRate'];
  minStakeAmount: InitializePoolInstructionDataArgs['minStakeAmount'];
  lockupPeriod: InitializePoolInstructionDataArgs['lockupPeriod'];
  enforceLockup: InitializePoolInstructionDataArgs['enforceLockup'];
  poolEndDate: InitializePoolInstructionDataArgs['poolEndDate'];
};

export function getInitializePoolInstruction<
  TAccountPool extends string,
  TAccountStakeMint extends string,
  TAccountRewardMint extends string,
  TAccountStakeVault extends string,
  TAccountRewardVault extends string,
  TAccountPayer extends string,
  TAccountTokenProgram extends string,
  TAccountSystemProgram extends string,
  TAccountRent extends string,
  TAccountProgramAuthority extends string,
  TProgramAddress extends Address = typeof STAKE_POOL_PROGRAM_ADDRESS,
>(
  input: InitializePoolInput<
    TAccountPool,
    TAccountStakeMint,
    TAccountRewardMint,
    TAccountStakeVault,
    TAccountRewardVault,
    TAccountPayer,
    TAccountTokenProgram,
    TAccountSystemProgram,
    TAccountRent,
    TAccountProgramAuthority
  >,
  config?: { programAddress?: TProgramAddress }
): InitializePoolInstruction<
  TProgramAddress,
  TAccountPool,
  TAccountStakeMint,
  TAccountRewardMint,
  TAccountStakeVault,
  TAccountRewardVault,
  TAccountPayer,
  TAccountTokenProgram,
  TAccountSystemProgram,
  TAccountRent,
  TAccountProgramAuthority
> {
  // Program address.
  const programAddress = config?.programAddress ?? STAKE_POOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
    stakeMint: { value: input.stakeMint ?? null, isWritable: false },
    rewardMint: { value: input.rewardMint ?? null, isWritable: false },
    stakeVault: { value: input.stakeVault ?? null, isWritable: true },
    rewardVault: { value: input.rewardVault ?? null, isWritable: true },
    payer: { value: input.payer ?? null, isWritable: true },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    rent: { value: input.rent ?? null, isWritable: false },
    programAuthority: {
      value: input.programAuthority ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA' as Address<'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'>;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }
  if (!accounts.rent.value) {
    accounts.rent.value =
      'SysvarRent111111111111111111111111111111111' as Address<'SysvarRent111111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.pool),
      getAccountMeta(accounts.stakeMint),
      getAccountMeta(accounts.rewardMint),
      getAccountMeta(accounts.stakeVault),
      getAccountMeta(accounts.rewardVault),
      getAccountMeta(accounts.payer),
      getAccountMeta(accounts.tokenProgram),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.rent),
      getAccountMeta(accounts.programAuthority),
    ],
    data: getInitializePoolInstructionDataEncoder().encode(
      args as InitializePoolInstructionDataArgs
    ),
    programAddress,
  } as InitializePoolInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountStakeMint,
    TAccountRewardMint,
    TAccountStakeVault,
    TAccountRewardVault,
    TAccountPayer,
    TAccountTokenProgram,
    TAccountSystemProgram,
    TAccountRent,
    TAccountProgramAuthority
  >);
}

export type ParsedInitializePoolInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The stake pool PDA */
    pool: TAccountMetas[0];
    /** The token mint being staked */
    stakeMint: TAccountMetas[1];
    /** The reward token mint */
    rewardMint: TAccountMetas[2];
    /** The pool's stake token vault */
    stakeVault: TAccountMetas[3];
    /** The pool's reward token vault */
    rewardVault: TAccountMetas[4];
    /** The account paying for rent (must be authorized admin) */
    payer: TAccountMetas[5];
    /** The token program */
    tokenProgram: TAccountMetas[6];
    /** The system program */
    systemProgram: TAccountMetas[7];
    /** Rent sysvar */
    rent: TAccountMetas[8];
    /** The program authority account (validates creator permission) */
    programAuthority: TAccountMetas[9];
  };
  data: InitializePoolInstructionData;
};

export function parseInitializePoolInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedInitializePoolInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 10) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      pool: getNextAccount(),
      stakeMint: getNextAccount(),
      rewardMint: getNextAccount(),
      stakeVault: getNextAccount(),
      rewardVault: getNextAccount(),
      payer: getNextAccount(),
      tokenProgram: getNextAccount(),
      systemProgram: getNextAccount(),
      rent: getNextAccount(),
      programAuthority: getNextAccount(),
    },
    data: getInitializePoolInstructionDataDecoder().decode(instruction.data),
  };
}
